import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useAppDispatch,
  useAppSelector,
  useShallowSelector,
} from '../../app/hooks';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import { dialogActions } from '../../reducers/dialogSlice';
import {
  AgentInterceptionType,
  InfoTransmissionMessage,
  RestaurantStaffInterventionStatus,
  StaffInterceptionType,
  messagingActions,
} from '../../reducers/messagingSlice';
import { orderActions } from '../../reducers/orderSlice';
import { OrderStatus } from '../../reducers/orderSlice.constants';
import {
  promptsSelector,
  selectAlreadySpokenTotalConstraint,
  selectTotalConstraint,
} from '../../selectors/menu';
import { selectRestaurantStaffIntervention } from '../../selectors/message';
import {
  selectCurrentTransactionCoupons,
  selectOrderValues,
  selectIsTotalSpokenInUpsellValid,
} from '../../selectors/order';
import {
  selectActiveRestaurantDetails,
  selectActiveRestaurantIsBilingual,
} from '../../selectors/restaurant';
import { getMessageWithTotal } from '../../utils/TTSMessage';
import { activeCartSelector } from '../../utils/cart';
import { LANGUAGES } from '../../utils/constants';
import useSendMessageHooks from '../../hooks/useSendMessage/useSendMessage.hooks';
import { pickNextIndex } from '../../utils/common';
import useLanguages from '../../hooks/useLanguages.hooks';
import { IntentCard, Intents, VISIBILITY_FACTOR } from '../../types/intent';
import useReadback from '../../hooks/useReadback/useReadback.hooks';
import useSlashCommandReminder from '../../hooks/useSlashCommandReminder.hooks';
import { selectIntentCards } from '../../selectors/menu';
import { DEFAULT_INTENT_CARDS } from '../../constants/intents';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';
import logger from '../../utils/logger';
import { selectIsAgentActionBlocked } from '../../redux/features/agentMode/agentMode.selector';

// Intent click/dispatch logic for the HITL 3.0 grouped bottom bar
// (IntentsSectionGrouped). Owned entirely by the new layout — deliberately
// independent of the OLD IntentsSection (which keeps its own copy as the
// flag-OFF production path) so the two can never affect each other. The
// duplication is intentional and disappears once the old layout is removed.
const useGroupedIntents = () => {
  const dispatch = useAppDispatch();
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  const cartActive = useSelector(activeCartSelector);
  const prompts = useSelector(promptsSelector);
  const bmTotalPrompt = useSelector(selectTotalConstraint);
  const alreadySpokenTotalPrompt = useSelector(
    selectAlreadySpokenTotalConstraint
  );
  const isTotalSpokenInUpsellValid = useAppSelector(
    selectIsTotalSpokenInUpsellValid
  );
  const currentTransactionId = useAppSelector(
    (state) => state.order.currentTransactionId
  );
  const currentTransactionCoupons = useAppSelector(
    selectCurrentTransactionCoupons
  );
  const currentTransactionCouponStatus = Object.values(
    currentTransactionCoupons
  );
  const allCouponApplied = useMemo(() => {
    return currentTransactionCouponStatus.every(
      (status) => status === OrderStatus.completed
    );
  }, [currentTransactionCouponStatus]);

  const transactions = useAppSelector((state) => state.order.transactions);
  const ITEM_BY_ITEM = useAppSelector((state) => state.config.ITEM_BY_ITEM);
  const completeClickCount = useAppSelector(
    (state) => state.order.completeClickCount
  );
  const { total } = useShallowSelector(selectOrderValues);
  const restaurantStaffIntervention = useSelector(
    selectRestaurantStaffIntervention
  );

  const { restaurantCode, restaurantName } = useSelector(
    selectActiveRestaurantDetails
  );
  const { isStaffInterventionHappening } = useIntervention();
  const isBilingual = useAppSelector(selectActiveRestaurantIsBilingual);
  const [downTime, setDownTime] = useState(0);
  const intentsStatus = useAppSelector((state) => state.dialog.intentsStatus);
  const DISABLE_INTENT_INTERVAL = useAppSelector(
    (state) => state.config.DISABLE_INTENT_INTERVAL
  );

  const currentLanguage = useAppSelector(
    (state) => state.language.currentLanguage
  );
  const isSpanish = currentLanguage === LANGUAGES.SPANISH;
  const intentCardsFromBM = useAppSelector(selectIntentCards);

  // Use intent cards from BM if available, otherwise fallback to default
  const INTENT_CARDS = intentCardsFromBM ?? DEFAULT_INTENT_CARDS;

  const numOfIntents = INTENT_CARDS.length;
  const { sendMessage } = useSendMessageHooks();
  const { sendAgentInterception } = useAgentInterception();
  const { sendLanguageSwitch } = useLanguages();
  const { handleReadBack } = useReadback();
  const { showSlashCommandReminder } = useSlashCommandReminder();

  useEffect(() => {
    // Re-seed whenever the array no longer matches the intent-card count. The
    // dialog slice is reset to `intentsStatus: []` on every restaurant / stage /
    // menu-version load (dialogSlice extraReducers). Keying this effect on
    // numOfIntents alone misses that reset when the card count is unchanged,
    // leaving intentsStatus empty — which renders every intent button disabled
    // (clickable resolves to false for every index).
    if (intentsStatus.length !== numOfIntents) {
      dispatch(
        dialogActions.initIntentsStatus(
          new Array(numOfIntents).fill({
            clickable: true,
            lastMessageIndex: undefined,
          })
        )
      );
    }
  }, [dispatch, numOfIntents, intentsStatus.length]);

  const onIntentDown = () => {
    setDownTime(new Date().valueOf());
  };

  const onIntentUp = (intent: IntentCard, idx: number) => {
    const timePressed = Math.floor((new Date().valueOf() - downTime) / 100);
    setDownTime(0);
    onIntentsClick(
      intent.title,
      intent.messages,
      timePressed,
      intent.intent,
      idx
    );
  };

  const setIntentClickable = (idx: number, status: boolean) => {
    dispatch(dialogActions.setIntentClickable({ idx, status }));
  };

  const sendAgentInterventionEvent = () => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.TTS_SENT,
      },
    });
  };

  const sendMetric = (message: string) => {
    const payload: Partial<InfoTransmissionMessage> = {
      data: { type: 'METRIC', message: message },
    };
    dispatch(messagingActions.sendInfo(payload as any));
  };

  const languageSwitch = function (intent: IntentCard) {
    sendAgentInterventionEvent();
    sendMetric(intent.intent + ' intent clicked');
    return sendLanguageSwitch(
      isSpanish ? LANGUAGES.ENGLISH : LANGUAGES.SPANISH
    );
  };

  const onIntentsClick = (
    title: string,
    messages: string[],
    cTimer: number,
    intent: string,
    idx: number
  ) => {
    // Staff intervention (agent- or backend-requested) hands the conversation to
    // a human — the AI/agent must stop talking to the customer. No intent may
    // fire TTS while it's happening. This is the single choke point for mouse
    // clicks (onIntentUp), the Alt+digit fast-path (fireIntentByIdx) and any
    // future caller, so one guard covers them all. The initiating "Request Staff"
    // click is unaffected: intervention isn't happening yet at that point.
    if (isStaffInterventionHappening) {
      return;
    }

    // HITL-1606: same choke point, same reason — outside Intercept an intent may
    // not fire. Guarded here rather than only on the pill's `disabled`, because
    // useIntentHotkeys' Alt+digit fast-path calls this function directly and would
    // otherwise speak to the customer, send `staff_intervention` or fire a readback
    // with the pill greyed out. Refusing the interception alone would not stop any
    // of those — they are dispatched before it.
    if (isAgentActionBlocked) {
      logger.debug({
        message: `Intent "${
          title || intent
        }" refused: the agent is not in Intercept mode`,
      });
      return;
    }
    setIntentClickable(idx, false);
    setTimeout(() => {
      setIntentClickable(idx, true);
    }, DISABLE_INTENT_INTERVAL);

    sendAgentInterventionEvent();
    sendMetric(title || intent + ' intent clicked');
    if (
      title === 'Request Staff' &&
      restaurantCode &&
      restaurantStaffIntervention === RestaurantStaffInterventionStatus.initial
    ) {
      dispatch(
        messagingActions.setRestaurantStaffIntervention(
          RestaurantStaffInterventionStatus.open
        )
      );
      dispatch(
        messagingActions.sendStaffInterception({
          data: {
            type: StaffInterceptionType.AGENT_REQUESTED_STAFF,
          },
        })
      );
    }

    if (title === 'Read Back') {
      handleReadBack({ timePressed: cTimer });
      return;
    }

    if (title === 'Confirm') {
      showSlashCommandReminder();
    }

    if (title === 'Complete') {
      dispatch(orderActions.increaseCompleteClickCount());
    }

    const messageIndex = pickNextIndex(
      messages.length,
      intentsStatus[idx].lastMessageIndex
    );
    let message = '';
    if (messageIndex !== undefined) {
      message = messages[messageIndex];
      dispatch(
        dialogActions.setIntentLastMessageIdx({
          idx,
          lastMessageIndex: messageIndex,
        })
      );
    }

    if (['Total', 'Complete'].includes(title)) {
      if (title === 'Complete') {
        const promptMessageFromMenu = bmTotalPrompt
          ? bmTotalPrompt['total-template']
          : prompts['total-template'];
        if (promptMessageFromMenu) {
          message = promptMessageFromMenu.phrase;
        }
      }

      // HITL-1570: when the donation upsell already read the check total (and
      // the cart hasn't changed since), getMessageWithTotal swaps in the
      // total-less completion prompt — but only where it would actually have
      // spoken the total; all of its guards still apply. Menus without the
      // constraint keep the existing path.
      message = getMessageWithTotal({
        message,
        ITEM_BY_ITEM,
        currentTransactionId,
        transactions,
        completeClickCount,
        total,
        allCouponApplied,
        title,
        restaurantCode,
        alreadySpokenMessage:
          title === 'Complete' &&
          isTotalSpokenInUpsellValid &&
          alreadySpokenTotalPrompt
            ? alreadySpokenTotalPrompt
            : undefined,
      });
    }
    message = message.split('{RESTAURANT_NAME}').join(restaurantName || '');
    sendMessage(message, { intent, timeDelta: cTimer });
  };

  const intents = INTENT_CARDS.filter((intent) => {
    if (intent.visibility !== undefined) {
      switch (intent.visibility) {
        case VISIBILITY_FACTOR.CART_ACTIVE:
          return cartActive;
        case VISIBILITY_FACTOR.CART_EMPTY:
          return !cartActive;
        default:
          return true;
      }
    } else {
      return true;
    }
  });

  // Keyboard fast-path (Alt+1…9). A tap has no hold duration, so press time is
  // 0; everything else — cooldown, message cycling, metrics — flows through the
  // same `onIntentsClick` as a mouse click. Request Staff is deliberately
  // excluded (escalation stays a mouse-only, deliberate action) and disabled
  // intents are ignored, mirroring the button's `disabled` guard.
  const fireIntentByIdx = (idx: number) => {
    const card = intents[idx];
    if (!card || card.intent === Intents.requestStaff) {
      return;
    }
    if (!intentsStatus[idx]?.clickable) {
      return;
    }
    onIntentsClick(card.title, card.messages, 0, card.intent, idx);
  };

  // Shortcut numbering: Request Staff has no key, so the keyboard-reachable
  // intents are numbered contiguously 1..N in display order (no gap where
  // Request Staff sits). The on-pill badge and the Alt+N digit both use this.
  // `shortcutNumberByIdx` maps an intent's original (filtered) index to its
  // 1-based shortcut number; `reachableIdxs[n-1]` is the inverse.
  const reachableIdxs = intents
    .map((card, idx) => ({ card, idx }))
    .filter(({ card }) => card.intent !== Intents.requestStaff)
    .map(({ idx }) => idx);

  const shortcutNumberByIdx: Record<number, number> = {};
  reachableIdxs.forEach((idx, i) => {
    shortcutNumberByIdx[idx] = i + 1;
  });

  const fireIntentByShortcut = (shortcutNumber: number) => {
    const idx = reachableIdxs[shortcutNumber - 1];
    if (idx === undefined) {
      return;
    }
    fireIntentByIdx(idx);
  };

  return {
    intents,
    intentsStatus,
    isBilingual,
    isSpanish,
    isStaffInterventionHappening,
    onIntentDown,
    onIntentUp,
    fireIntentByIdx,
    fireIntentByShortcut,
    shortcutNumberByIdx,
    languageSwitch,
  };
};

export default useGroupedIntents;
